import appMusic from "@/assets/sounds/AppMusic.mp3";
import bonus1 from "@/assets/sounds/bonus1.wav";
import gameEnd from "@/assets/sounds/GameEnd.wav";
import gameplayMusic from "@/assets/sounds/GameplayMusic.mp3";
// import generalTap from "@/assets/sounds/GeneralTap.wav";
import scratchShort from "@/assets/sounds/ScratchShort.wav";
import spin from "@/assets/sounds/Spin.wav";
// import spinstop from "@/assets/sounds/Spinstop.mp3";
import spinstopAlt from "@/assets/sounds/Spinstopalt.mp3";
import treasure1 from "@/assets/sounds/Treasure1.mp3";
import treasure10 from "@/assets/sounds/Treasure10.mp3";
import treasure11 from "@/assets/sounds/Treasure11.mp3";
import treasure12 from "@/assets/sounds/Treasure12.mp3";
import treasure13 from "@/assets/sounds/Treasure13.mp3";
import treasure2 from "@/assets/sounds/Treasure2.mp3";
import treasure3 from "@/assets/sounds/Treasure3.mp3";
import treasure4 from "@/assets/sounds/Treasure4.mp3";
import treasure5 from "@/assets/sounds/Treasure5.mp3";
import treasure6 from "@/assets/sounds/Treasure6.mp3";
import treasure7 from "@/assets/sounds/Treasure7.mp3";
import treasure8 from "@/assets/sounds/Treasure8.mp3";
import treasure9 from "@/assets/sounds/Treasure9.mp3";
import { PersistentData } from "./PersistentData";
import ticking from "@/assets/sounds/ticking.mp3";
import explosion from "@/assets/sounds/explosion.wav";


interface SoundFile {
  src: string;
  /**
   * max volume of the sound
   * @default 1
   */
  volume?: number;
}

const backgroundSounds = {
  appMusic: {
    src: appMusic,
    volume: 0.5,
  } satisfies SoundFile,
  gameplayMusic: {
    src: gameplayMusic,
    volume: 0.5,
  } satisfies SoundFile,
};
export type BackgroundSoundName = keyof typeof backgroundSounds;

const soundEffects = {
  bonus1: {
    src: bonus1,
  } satisfies SoundFile,
  gameEnd: {
    src: gameEnd,
  } satisfies SoundFile,
  // generalTap: {
  //   src: generalTap,
  // } satisfies SoundFile,
  scratchShort: {
    src: scratchShort,
  } satisfies SoundFile,
  spin: {
    src: spin,
  } satisfies SoundFile,
  // spinstop: {
  //   src: spinstop,
  // } satisfies SoundFile,
  spinstopAlt: {
    src: spinstopAlt,
  } satisfies SoundFile,
  treasure1: {
    src: treasure1,
  } satisfies SoundFile,
  treasure2: {
    src: treasure2,
  } satisfies SoundFile,
  treasure3: {
    src: treasure3,
  } satisfies SoundFile,
  treasure4: {
    src: treasure4,
  } satisfies SoundFile,
  treasure5: {
    src: treasure5,
  } satisfies SoundFile,
  treasure6: {
    src: treasure6,
  } satisfies SoundFile,
  treasure7: {
    src: treasure7,
  } satisfies SoundFile,
  treasure8: {
    src: treasure8,
  } satisfies SoundFile,
  treasure9: {
    src: treasure9,
  } satisfies SoundFile,
  treasure10: {
    src: treasure10,
  } satisfies SoundFile,
  treasure11: {
    src: treasure11,
  } satisfies SoundFile,
  treasure12: {
    src: treasure12,
  } satisfies SoundFile,
  treasure13: {
    src: treasure13,
  } satisfies SoundFile,
  explosion: {
    src: explosion,
  } satisfies SoundFile,
  ticking: {
    src: ticking,
    volume: 0.2,
  } satisfies SoundFile,

};
export type SoundEffectName = keyof typeof soundEffects;

const DEFAULT_FADE_TIME = 500;

const audioContext = new AudioContext();

class Sound extends EventTarget {
  readonly source: AudioBufferSourceNode;
  readonly gainNode: GainNode;

  static async fromFile(file: SoundFile, name?: string) {
    const arrayBuffer = await fetch(file.src).then((res) => res.arrayBuffer());
    const buffer = await audioContext.decodeAudioData(arrayBuffer);

    return new Sound(buffer, file.volume ?? 1, name);
  }

  private constructor(
    buffer: AudioBuffer,
    readonly maxVolume: number,
    readonly name: string | undefined = undefined
  ) {
    super();

    this.name = name;

    const analyser = audioContext.createAnalyser();
    this.source = audioContext.createBufferSource();
    this.source.buffer = buffer;
    this.gainNode = audioContext.createGain();

    this.source.connect(analyser);
    analyser.connect(this.gainNode);
    this.gainNode.connect(audioContext.destination);

    this.volume = this.maxVolume;

    this.source.addEventListener("ended", () => {
      this.dispatchEvent(new Event("ended"));
    });
  }

  playing = false;

  sourceStarted = false;
  play() {
    audioContext.resume();

    if (!this.sourceStarted) {
      this.source.start();
      this.sourceStarted = true;
    }
    this.playing = true;
  }

  stop() {
    this.source.stop();
    this.sourceStarted = false;
    this.playing = false;
    this.source.disconnect();
    this.gainNode.disconnect();
  }

  set loop(loop: boolean) {
    this.source.loop = loop;
  }

  set volume(volume: number) {
    this.gainNode.gain.value = Math.min(this.maxVolume, Math.max(0, volume));
  }

  get volume() {
    return this.gainNode.gain.value;
  }

  private fadingOut = false;

  fadeOut(stopAfterFade = true) {
    if (this.fadingOut) {
      return;
    }

    this.playing = false;
    this.fadingOut = true;

    this.gainNode.gain.linearRampToValueAtTime(
      0.0001,
      audioContext.currentTime + DEFAULT_FADE_TIME / 1000
    );

    setTimeout(() => {
      if (!this.fadingOut) {
        this.fadeIn();
        return;
      }
      if (stopAfterFade) {
        this.stop();
      }
      this.fadingOut = false;
    }, DEFAULT_FADE_TIME);
  }

  async fadeIn() {
    if (this.fadingOut) {
      this.fadingOut = false;
      return;
    }

    this.volume = 0;
    await this.play();

    this.gainNode.gain.linearRampToValueAtTime(
      this.maxVolume,
      audioContext.currentTime + DEFAULT_FADE_TIME / 1000
    );
  }
}

export class BackgroundSoundPlayer {
  private static sound: Sound | undefined;

  static async play(soundName: BackgroundSoundName) {
    if (this.sound?.name === soundName && this.sound.playing) {
      return;
    }

    const newSound = await Sound.fromFile(
      backgroundSounds[soundName],
      soundName
    );
    newSound.loop = true;

    this.sound?.fadeOut();
    newSound.fadeIn();

    this.sound = newSound;
  }

  static stop() {
    this.sound?.fadeOut();
  }

  private static pausedSound: Sound | undefined;
  // this function is for Android to call when the app is resumed
  static async resume() {
    const backgroundMusicEnabled =
      (await PersistentData.get("backgroundMusicEnabled")) ?? true;
    if (backgroundMusicEnabled) {
      this.pausedSound?.fadeIn();
      this.pausedSound = undefined;
    }
  }

  // this function is for Android to call when the app is paused
  static async pause() {
    this.pausedSound = this.sound;
    this.pausedSound?.fadeOut(false);
  }
}

export class SoundEffectPlayer {
  private static sounds: Sound[] = [];

  static async play(soundName: SoundEffectName, loop = false) {
    const newSound = await Sound.fromFile(soundEffects[soundName]);
    newSound.loop = loop;
    newSound.play();

    this.sounds.push(newSound);

    if (!loop) {
      newSound.addEventListener(
        "ended",
        () => {
          this.sounds = this.sounds.filter((a) => a !== newSound);
        },
        { once: true }
      );
    }

    return () => {
      newSound.stop();
      this.sounds = this.sounds.filter((a) => a !== newSound);
    };
  }

  static stop() {
    this.sounds.forEach((sound) => {
      sound.fadeOut();
    });
  }
}